import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  RICH_HTML_BOUNDS_MESSAGE, RICH_HTML_SANDBOX, buildRichHtmlDocument,
  readThemeTokens, thumbnailTransform,
} from '../lib/richHtmlFrame';
import { fetchBonusDescriptionHtml } from '../lib/bonusDescriptionHtml';

// Şablonun "masaüstü" genişliği: çerçeve bu genişlikte çizilir ve kutuya
// sığacak kadar küçültülür. Dar kutuda şablonun mobil kırılımına düşmek
// yerine tasarımın kendisi (başlık, renkler, liste) küçük hâliyle görünür.
export const BONUS_THUMB_VIRTUAL_WIDTH = 720;

// Görseli olmayan kampanyada kart/vitrin görsel alanına HTML şablon çizilir
// mi: açıklama HTML ve panelde "Kartta da tasarımıyla göster" açık.
export const bonusHasHtmlArt = (bonus) => !!(
  bonus && !bonus.image && bonus.description_html && bonus.card_html_enabled
);

/**
 * Kampanyanın HTML açıklamasını bir görsel gibi, kutuyu kaplayan küçültülmüş
 * bir çerçevede çizer (bonus kartının ve vitrin şeridinin görsel alanı).
 *
 * Konumlanmış bir kabın içinde ``absolute inset-0`` durur. Çerçeve etkileşim
 * almaz (``pointer-events: none``): kart bir bağlantı ya da düğmedir ve
 * tıklama ona gitmelidir. Şablon gelene kadar (ya da alınamazsa) ``children``
 * yedek görünüm olarak çizilir — kart hiçbir anda boş kalmaz. ``enabled``
 * kapalıyken istek atılmaz, yalnızca yedek görünüm çizilir.
 *
 * ``html`` verilirse şablon uçtan istenmez: panel ham açıklamayı zaten
 * elinde tutar ve oyuncu ucu pasif kampanyada 404 döndürür — panelin kartı
 * pasif kampanyayı da çizmelidir.
 */
const BonusHtmlThumb = ({
  bonusId, html: providedHtml, title = 'Kampanya', children = null, enabled = true,
  virtualWidth = BONUS_THUMB_VIRTUAL_WIDTH,
}) => {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const [html, setHtml] = useState('');
  // Şablonun dolu alanı (çerçeve bildirir). Gelene kadar çerçeve görünmez:
  // aksi hâlde dar şablon bir an geniş kutunun solunda küçük bir sütun
  // olarak çizilip sonra yerine atlardı.
  const [bounds, setBounds] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setHtml('');
    setBounds(null);
    if (!enabled) return undefined;
    if (typeof providedHtml === 'string') {
      setHtml(providedHtml);
      return undefined;
    }
    let cancelled = false;
    fetchBonusDescriptionHtml(bonusId)
      .then((value) => { if (!cancelled) setHtml(value); })
      .catch(() => { if (!cancelled) setHtml(''); });
    return () => { cancelled = true; };
  }, [bonusId, enabled, providedHtml]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => setSize({ width: box.clientWidth, height: box.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (event.data?.type !== RICH_HTML_BOUNDS_MESSAGE) return;
      const { left, right, top, bottom } = event.data;
      if (![left, right, top, bottom].every(Number.isFinite) || right <= left) return;
      setBounds({ left, right, top, bottom });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const srcDoc = useMemo(() => {
    if (!html) return '';
    const body = typeof document !== 'undefined' ? document.body : null;
    const computed = body && typeof window !== 'undefined'
      ? window.getComputedStyle(body)
      : null;
    return buildRichHtmlDocument(html, {
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      fontFamily: computed?.fontFamily || '',
      color: computed?.color || '',
      tokens: readThemeTokens(),
      thumbnail: true,
    });
  }, [html]);

  const ready = enabled && !!srcDoc && size.width > 0 && size.height > 0;
  const fit = ready ? thumbnailTransform({
    box: size, bounds, virtualWidth,
  }) : null;
  const shown = ready && !!bounds;
  return (
    <span
      ref={boxRef}
      className="absolute inset-0 block overflow-hidden"
      data-testid="bonus-html-thumb"
    >
      {!shown && children}
      {ready && (
        <iframe
          ref={frameRef}
          title={title}
          sandbox={RICH_HTML_SANDBOX}
          srcDoc={srcDoc}
          scrolling="no"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute left-0 top-0 block border-0 bg-transparent"
          style={{
            width: virtualWidth,
            height: fit.frameHeight,
            transform: fit.transform,
            transformOrigin: '0 0',
            pointerEvents: 'none',
            opacity: shown ? 1 : 0,
          }}
        />
      )}
    </span>
  );
};

export default BonusHtmlThumb;
